import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Calendar,
  Briefcase,
  History,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SolicitudDetalle = ({ solicitud, tipo, onClose }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [accionActual, setAccionActual] = useState(null);
  const [comentario, setComentario] = useState('');

  const handleAccion = async () => {
    setLoading(true);
    try {
      const endpoint = tipo === 'socios' 
        ? `${API_URL}/api/interno/solicitudes/socios/${solicitud.id}/accion`
        : `${API_URL}/api/interno/solicitudes/guia/${solicitud.id}/accion`;
      
      await axios.post(endpoint, {
        accion: accionActual,
        comentario: comentario || null
      }, { headers: getAuthHeaders() });

      toast.success(`Solicitud ${accionActual === 'aprobar' ? 'aprobada' : accionActual === 'rechazar' ? 'rechazada' : 'devuelta'} correctamente`);
      setDialogOpen(false);
      onClose();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al procesar la acción');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (accion) => {
    setAccionActual(accion);
    setComentario('');
    setDialogOpen(true);
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      pendiente: 'bg-yellow-100 text-yellow-700',
      aprobado: 'bg-green-100 text-green-700',
      rechazado: 'bg-red-100 text-red-700',
      devuelto: 'bg-orange-100 text-orange-700'
    };
    return (
      <Badge className={`${estilos[estado] || estilos.pendiente} text-sm`}>
        {estado.charAt(0).toUpperCase() + estado.slice(1)}
      </Badge>
    );
  };

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const datos = solicitud.datos || {};

  return (
    <div className="space-y-6" data-testid="solicitud-detalle">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onClose}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[#1E3876]">
              {tipo === 'socios' ? 'Solicitud de Alta de Socio' : 'Solicitud Guía Profesionales'}
            </h1>
            <p className="text-gray-500 text-sm">ID: {solicitud.id}</p>
          </div>
        </div>
        {getEstadoBadge(solicitud.estado)}
      </div>

      {/* Acciones */}
      {solicitud.estado === 'pendiente' && (
        <Card className="cao-card border-l-4 border-l-[#2853B9]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-[#1E3876]">Acciones disponibles</span>
              <div className="flex gap-2">
                <Button
                  onClick={() => openDialog('aprobar')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Aprobar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openDialog('devolver')}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Devolver
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openDialog('rechazar')}
                  className="border-red-500 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rechazar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Datos del solicitante */}
        <Card className="cao-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1E3876]">
              <User className="w-5 h-5" />
              Datos del Solicitante
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-500 text-xs">Nombre</Label>
                <p className="font-medium">{solicitud.user_nombre}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Email</Label>
                <p className="font-medium">{solicitud.user_email}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Fecha solicitud</Label>
                <p className="font-medium">{formatFecha(solicitud.created_at)}</p>
              </div>
              {tipo === 'socios' && (
                <>
                  <div>
                    <Label className="text-gray-500 text-xs">Categoría</Label>
                    <p className="font-medium">{datos.categoria_socio?.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">CUIT/CUIL</Label>
                    <p className="font-medium">{datos.cuit_cuil}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Fecha Nacimiento</Label>
                    <p className="font-medium">{datos.fecha_nacimiento}</p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Información profesional */}
        <Card className="cao-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1E3876]">
              <Briefcase className="w-5 h-5" />
              Información Profesional
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {tipo === 'socios' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Matrícula Nacional</Label>
                  <p className="font-medium">{datos.matricula_nacional || 'No informada'}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Matrícula Provincial</Label>
                  <p className="font-medium">{datos.matricula_provincial || 'No informada'}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Subespecialidad Principal</Label>
                  <p className="font-medium">{datos.subespecialidad_principal}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Opción de Pago</Label>
                  <p className="font-medium">{datos.opcion_pago?.replace(/_/g, ' ')}</p>
                </div>
                {datos.subespecialidad_2 && (
                  <div>
                    <Label className="text-gray-500 text-xs">Subespecialidad 2</Label>
                    <p className="font-medium">{datos.subespecialidad_2}</p>
                  </div>
                )}
                {datos.subespecialidad_3 && (
                  <div>
                    <Label className="text-gray-500 text-xs">Subespecialidad 3</Label>
                    <p className="font-medium">{datos.subespecialidad_3}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-500 text-xs">Nombre completo</Label>
                  <p className="font-medium">{datos.nombre_apellido}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Subespecialidad Principal</Label>
                  <p className="font-medium">{datos.subespecialidad_principal}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Breve CV</Label>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{datos.breve_cv}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dirección */}
        <Card className="cao-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1E3876]">
              <MapPin className="w-5 h-5" />
              Dirección
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tipo === 'socios' ? (
              <div className="space-y-2">
                <p className="font-medium">{datos.direccion}</p>
                <p className="text-gray-600">
                  {datos.localidad}, {datos.provincia} ({datos.codigo_postal})
                </p>
                <p className="text-gray-600">{datos.pais}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-500 text-xs">Establecimiento Principal</Label>
                  <p className="font-medium">{datos.direccion_principal?.nombre_establecimiento}</p>
                  <p className="text-gray-600">
                    {datos.direccion_principal?.calle} {datos.direccion_principal?.numero}
                    {datos.direccion_principal?.piso_depto && `, ${datos.direccion_principal.piso_depto}`}
                  </p>
                  <p className="text-gray-600">
                    {datos.direccion_principal?.localidad}, {datos.direccion_principal?.provincia}
                  </p>
                  <p className="text-gray-600">Tel: {datos.direccion_principal?.telefono_pacientes}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documentación (solo socios) */}
        {tipo === 'socios' && (
          <Card className="cao-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1E3876]">
                <FileText className="w-5 h-5" />
                Documentación
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { key: 'documento_identidad', label: 'Documento de Identidad' },
                  { key: 'documento_matricula', label: 'Matrícula Profesional' },
                  { key: 'titulo_medico', label: 'Título de Médico' },
                  { key: 'titulo_especialista', label: 'Título de Especialista' },
                  { key: 'certificado_residencia', label: 'Certificado de Residencia' }
                ].map((doc) => (
                  <div key={doc.key} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{doc.label}</span>
                    {datos[doc.key] ? (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Adjunto
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const win = window.open();
                            win.document.write(`
                              <html>
                                <head><title>${doc.label}</title></head>
                                <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;">
                                  ${datos[doc.key].startsWith('data:application/pdf') 
                                    ? `<iframe src="${datos[doc.key]}" style="width:100%;height:100vh;border:none;"></iframe>`
                                    : `<img src="${datos[doc.key]}" style="max-width:100%;max-height:100vh;object-fit:contain;" />`
                                  }
                                </body>
                              </html>
                            `);
                          }}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Ver
                        </Button>
                      </div>
                    ) : (
                      <Badge variant="outline" className="text-gray-400">
                        No adjunto
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Historial de cambios */}
      <Card className="cao-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1E3876]">
            <History className="w-5 h-5" />
            Historial de Cambios
          </CardTitle>
        </CardHeader>
        <CardContent>
          {solicitud.historial && solicitud.historial.length > 0 ? (
            <div className="space-y-4">
              {solicitud.historial.slice().reverse().map((item, index) => (
                <div key={index} className="flex gap-4 items-start">
                  <div className="w-2 h-2 rounded-full bg-[#2853B9] mt-2"></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#1E3876]">
                        {item.accion.charAt(0).toUpperCase() + item.accion.slice(1)}
                      </span>
                      <span className="text-xs text-gray-400">{formatFecha(item.fecha)}</span>
                    </div>
                    <p className="text-sm text-gray-600">{item.comentario}</p>
                    <p className="text-xs text-gray-400">Por: {item.usuario_nombre}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Sin historial de cambios</p>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmación */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {accionActual === 'aprobar' && 'Aprobar Solicitud'}
              {accionActual === 'rechazar' && 'Rechazar Solicitud'}
              {accionActual === 'devolver' && 'Devolver para Correcciones'}
            </DialogTitle>
            <DialogDescription>
              {accionActual === 'aprobar' && 'La solicitud será aprobada y el solicitante será notificado.'}
              {accionActual === 'rechazar' && 'La solicitud será rechazada definitivamente.'}
              {accionActual === 'devolver' && 'La solicitud será devuelta al usuario para que realice correcciones.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Comentario {accionActual !== 'aprobar' && '(requerido)'}</Label>
            <Textarea
              placeholder={
                accionActual === 'aprobar' 
                  ? 'Comentario opcional...' 
                  : 'Indique el motivo o las correcciones necesarias...'
              }
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAccion}
              disabled={loading || (accionActual !== 'aprobar' && !comentario)}
              className={
                accionActual === 'aprobar' ? 'bg-green-600 hover:bg-green-700' :
                accionActual === 'rechazar' ? 'bg-red-600 hover:bg-red-700' :
                'bg-orange-600 hover:bg-orange-700'
              }
            >
              {loading ? 'Procesando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolicitudDetalle;
